/* eslint-disable react/destructuring-assignment -- Will make the code unnecessarily complex */
import { AudioPlayerSkeleton } from "@circle-react-shared/MediaPlayer/AudioPlayer/AudioPlayerSkeleton";
import {
  isAudioContentType,
  isVideoContentType,
} from "@circle-react-shared/MediaPlayer/mediaType";
import { useMediaConfig } from "@circle-react-shared/MediaPlayer/VideoPlayer/hooks/useMediaConfig";
import { VideoPlayerSkeleton } from "@circle-react-shared/MediaPlayer/VideoPlayer/VideoPlayerSkeleton";
import type { TranscriptsPlayerProps } from "@circle-react-uikit/TranscriptsPlayer/TranscriptsPlayerComponent";
import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
import { Suspense } from "react";

const AudioPlayer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "MediaChromeAudioPlayer" */ "@circle-react-shared/MediaPlayer/AudioPlayer"
  ).then(module => ({ default: module.AudioPlayer })),
);

const VideoPlayer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "MediaChromeVideoPlayer" */ "@circle-react-shared/MediaPlayer/VideoPlayer"
  ).then(module => ({ default: module.VideoPlayer })),
);

const TranscriptsPlayerComponent = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "TranscriptsPlayerPlyr" */ "@circle-react-uikit/TranscriptsPlayer/TranscriptsPlayerComponent"
  ).then(module => ({ default: module.TranscriptsPlayerComponent })),
);

export const TranscriptsPlayerWrapper = (props: TranscriptsPlayerProps) => {
  const isVideoFile = isVideoContentType(props.type);
  const isAudioFile = isAudioContentType(props.type);
  const { posterUrl, isDownloadable, mediaTranscriptId } = useMediaConfig({
    signedId: props.signedId,
    posterUrl: props.poster,
    isDownloadable: props.isDownloadable,
    mediaTranscriptId: props.mediaTranscriptId,
  });

  if (isVideoFile)
    return (
      <Suspense
        fallback={
          <VideoPlayerSkeleton
            aspectRatio={props.aspectRatio}
            posterUrl={posterUrl}
          />
        }
      >
        <VideoPlayer
          src={props.src}
          contentType={props.type}
          mediaTranscoding={props.mediaTranscoding}
          mediaTranscriptId={mediaTranscriptId}
          overlayActions={props.overlayActions}
          downloadable={isDownloadable}
          downloadUrl={props.downloadUrl}
          lockAspectRatio={props.lockAspectRatio}
          maxHeight={props.maxHeight}
          posterUrl={posterUrl}
          chaptersUrl={props.chaptersUrl}
          previewThumbnailsUrl={props.previewThumbnailsUrl}
          signedId={props.signedId}
          variants={props.variants}
          onProgress={props.onProgress}
          preload={props.preload}
          customizeMediaOverlayTooltipProps={
            props.customizeMediaOverlayTooltipProps
          }
          transcriptContainerClassName={props.transcriptContainerClassName}
        />
      </Suspense>
    );

  if (isAudioFile)
    return (
      <Suspense fallback={<AudioPlayerSkeleton />}>
        <AudioPlayer
          downloadable={props.isDownloadable}
          downloadUrl={props.downloadUrl}
          mediaTranscriptId={props.mediaTranscriptId}
          src={props.src}
          chaptersUrl={props.chaptersUrl}
          onProgress={props.onProgress}
          overlayActions={props.overlayActions}
          posterUrl={props.poster}
          preload={props.preload}
          signedId={props.signedId}
          title={props.title}
          customizeMediaOverlayTooltipProps={
            props.customizeMediaOverlayTooltipProps
          }
        />
      </Suspense>
    );

  return (
    <Suspense
      fallback={
        <VideoPlayerSkeleton
          aspectRatio={props.aspectRatio}
          posterUrl={props.poster}
        />
      }
    >
      <TranscriptsPlayerComponent {...props} />
    </Suspense>
  );
};
